"use strict";
/**
 * The patient analytics event registry: every event the patient-facing
 * product records, shared by the browser client and the API so neither side
 * can invent a name the other doesn't know. The API drops events whose name
 * is not listed here, and drops properties whose key is not listed for that
 * event.
 *
 * Naming: object_action, snake_case, past tense ("order_paid"). Properties
 * carry ids, enums, counts, booleans and amounts only — never text a patient
 * typed (Ava questions, messages, notes), never contact details.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.deriveChannel = exports.ANALYTICS_CHANNELS = exports.referrerParts = exports.pathOnly = exports.sanitizeQueryParams = exports.isAdNetworkParam = exports.UTM_KEYS = exports.CLICK_ID_KEYS = exports.looksSensitiveValue = exports.SENSITIVE_PARAM_KEY = exports.MAX_PROPERTIES = exports.MAX_PROPERTY_STRING = exports.MAX_QUERY_PARAMS = exports.REDACTED = exports.surfaceForPath = exports.isAnalyticsEventName = exports.ANALYTICS_EVENT_NAMES = exports.ANALYTICS_EVENTS = exports.ANALYTICS_SURFACES = void 0;
exports.ANALYTICS_SURFACES = ['storefront', 'patient_login', 'portal', 'checkout', 'payment', 'server'];
const client = (surface, description, properties = []) => ({ surface, source: 'client', description, properties });
const server = (description, properties = []) => ({ surface: 'server', source: 'server', description, properties });
exports.ANALYTICS_EVENTS = {
    // --- Every surface ---
    page_viewed: client('auto', 'A patient-facing page was shown (automatic, on route change).'),
    // --- Storefront (the first eight are bridged from the legacy DtpStorefrontEvent types) ---
    storefront_session_started: client('storefront', 'First storefront page of a tab session for a clinic (legacy session_start).', ['variant', 'entryPath']),
    storefront_landing_viewed: client('storefront', 'Educational storefront landing page shown (legacy landing_view).', ['variant']),
    category_viewed: client('storefront', 'Storefront category page shown (legacy category_view).', ['variant', 'categorySlug']),
    product_family_viewed: client('storefront', 'Product family page shown (legacy family_view).', ['variant', 'categorySlug', 'familySlug']),
    dose_finder_completed: client('storefront', 'Dose finder answered on a family page (legacy dose_finder_complete).', ['variant', 'categorySlug', 'familySlug', 'productId']),
    cart_item_added: client('storefront', 'Product added to the cart (legacy add_to_cart).', ['variant', 'productId', 'source']),
    product_viewed: client('storefront', 'Product detail page shown.', ['productId']),
    cart_viewed: client('storefront', 'Cart drawer or cart page opened.', ['itemCount']),
    checkout_started: client('checkout', 'Checkout opened with items in the cart (once per checkout visit).', ['itemCount', 'authenticated']),
    checkout_step_viewed: client('checkout', 'A checkout step was shown (legacy checkout_step).', ['variant', 'step']),
    checkout_order_placed: client('checkout', 'Checkout created the order and moved to payment (legacy order_created).', ['variant', 'orderId']),
    checkout_completed: client('checkout', 'Patient returned from the payment form to the order confirmation.', ['orderId', 'status']),
    // --- Patient sign-in ---
    login_viewed: client('patient_login', 'Patient sign-in page shown.', ['entry']),
    login_method_selected: client('patient_login', 'Patient chose a sign-in method.', ['method']),
    // --- Portal ---
    nav_clicked: client('portal', 'Portal navigation used.', ['destination', 'placement']),
    buy_again_clicked: client('portal', 'Buy again on a past order, with what made it into the cart.', ['orderId', 'added', 'skipped']),
    pay_now_clicked: client('portal', 'Pay now on an order awaiting payment.', ['orderId', 'isRefill']),
    order_tracking_clicked: client('portal', 'Carrier tracking opened for an order.', ['orderId']),
    auto_refill_setup_opened: client('portal', 'Auto-refill setup opened.', ['placement']),
    recommendation_viewed: client('portal', 'A product recommendation was shown (once per item per session).', ['kind', 'productId']),
    recommendation_added_to_cart: client('portal', 'A recommended product was added to the cart.', ['kind', 'productId']),
    lab_result_viewed: client('portal', 'A lab result report was opened.', ['resultId', 'flaggedCount']),
    health_overview_interacted: client('portal', 'The health overview on Home was used.', ['action']),
    treatments_viewed: client('portal', 'Treatments page shown.', ['count']),
    support_chat_opened: client('portal', 'Support chat opened.', ['placement']),
    ava_opened: client('portal', 'Ask Ava opened.', ['placement']),
    ava_question_sent: client('portal', 'A question was sent to Ava (never its text).', ['starter']),
    ava_next_step_clicked: client('portal', 'A next-step link in an Ava answer was followed.', ['destination']),
    billing_viewed: client('portal', 'Billing page shown.', ['cardCount']),
    card_add_started: client('portal', 'Add-a-card form opened.'),
    receipt_viewed: client('portal', 'A receipt was opened.', ['receiptId']),
    membership_viewed: client('portal', 'Membership page shown.', ['status']),
    // --- Server outcomes ---
    signup_completed: server('A patient account was created at checkout.', ['physicianId']),
    order_created: server('An order for a patient was created.', ['orderId', 'physicianId', 'itemCount', 'total', 'source']),
    order_paid: server('A patient order payment succeeded.', ['orderId', 'amount', 'isRefill']),
    login_succeeded: server('A patient signed in.', ['method']),
    login_failed: server('A patient sign-in attempt failed.', ['method', 'reason']),
    refill_plan_created: server('Auto-refill plan created.', ['planId', 'intervalDays']),
    refill_plan_paused: server('Auto-refill plan paused.', ['planId']),
    refill_plan_resumed: server('Auto-refill plan resumed.', ['planId']),
    refill_plan_skipped: server('Next auto-refill skipped.', ['planId']),
    refill_plan_cancelled: server('Auto-refill plan cancelled.', ['planId']),
    refill_plan_interval_changed: server('Auto-refill interval changed.', ['planId', 'intervalDays']),
    refill_order_prepared: server('A due auto-refill order was prepared for payment.', ['planId', 'cycle', 'orderId']),
    refill_requested: server('Patient asked the clinic for a refill.', ['productId', 'destination']),
    refill_request_decided: server('The clinic decided a refill request.', ['decision', 'destination']),
    care_message_sent: server('A care-team message was sent.', ['author', 'destination']),
    ava_turn_completed: server('Ava answered a patient question.', ['requests', 'costUsd', 'stopReason']),
    card_added: server('A card was saved to the patient wallet.'),
    card_removed: server('A saved card was removed.'),
    default_card_set: server('A saved card became the default.'),
    receipt_emailed: server('A receipt was emailed to the patient on request.'),
    notification_prefs_changed: server('A notification preference was changed.', ['category', 'channel', 'value']),
    texts_stopped_web: server('Patient stopped texts from the web opt-out page.'),
    sign_in_method_added: server('Patient added a sign-in method.', ['method']),
    account_updated: server('Patient updated their account (field names only).', ['fields']),
    membership_changed: server('Patient started, paused, resumed, skipped, re-tiered or cancelled a membership.', ['action']),
    notification_sent: server('A patient notification was sent, skipped or failed (no recipient).', ['kind', 'channel', 'status']),
};
exports.ANALYTICS_EVENT_NAMES = Object.keys(exports.ANALYTICS_EVENTS);
function isAnalyticsEventName(name) {
    return typeof name === 'string' && Object.prototype.hasOwnProperty.call(exports.ANALYTICS_EVENTS, name);
}
exports.isAnalyticsEventName = isAnalyticsEventName;
/** Portal page paths, after any /dtp/:physicianId prefix. */
const PORTAL_PATH = /^\/(home|portal|account|orders|billing|messages|support|assistant|treatments|refills|results|membership)(\/|$)/;
/** Surface of a page path, for events registered with surface 'auto'. */
function surfaceForPath(path) {
    if (/^\/patient\/login(\/|$)/.test(path))
        return 'patient_login';
    if (/^\/payment(\/|$)/.test(path))
        return 'payment';
    const local = path.replace(/^\/dtp\/[^/]+/, '') || '/';
    if (/^\/checkout(\/|$)/.test(local))
        return 'checkout';
    if (/^\/orders\/[^/]+\/confirmation$/.test(local))
        return 'checkout';
    if (PORTAL_PATH.test(local))
        return 'portal';
    return 'storefront';
}
exports.surfaceForPath = surfaceForPath;
// ---------------------------------------------------------------------------
// Privacy rules, shared so the browser and the API sanitize identically. The
// API is authoritative (it re-applies all of these); the client mirrors them
// so a credential never leaves the browser in the first place.
exports.REDACTED = '[redacted]';
exports.MAX_QUERY_PARAMS = 30;
exports.MAX_PROPERTY_STRING = 200;
exports.MAX_PROPERTIES = 30;
/**
 * Query keys whose value is a credential or personal data. The key is kept
 * (so we can see that a link carried one), the value never is. Covers the
 * app's own token-bearing params: pi (staff portal handoff), token, sig and
 * the address fields of a clinic's signed auto-import link, k (lab result
 * link key), email, payment_intent_client_secret (payment return URL).
 */
exports.SENSITIVE_PARAM_KEY = /token|^code$|otp|^pi$|^k$|sig|signature|secret|key|auth|pass|session|jwt|email|phone|dob|birth|name|address|street|city|^state$|zip|ssn|card|account/i;
const JWT_LIKE = /^[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/;
const EMAIL_LIKE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PHONE_LIKE = /^\+?[\d\s().-]{10,}$/;
const LONG_RANDOM = /^[A-Za-z0-9_\-+/=%.]{41,}$/;
/** True when a value looks like a credential or contact detail whatever its key. */
function looksSensitiveValue(value) {
    const v = value.trim();
    if (JWT_LIKE.test(v) || EMAIL_LIKE.test(v) || LONG_RANDOM.test(v))
        return true;
    return PHONE_LIKE.test(v) && v.replace(/\D/g, '').length >= 10;
}
exports.looksSensitiveValue = looksSensitiveValue;
/** Ad-network click ids, captured as attribution unless the browser sends Global Privacy Control. */
exports.CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'li_fat_id', 'twclid', 'epik', 'sccid'];
exports.UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
/** Other ad-network params dropped under Global Privacy Control. */
const AD_PARAM = /^(_gl|_ga|dclid|yclid|mc_eid|mc_cid|igshid|ad_id|adset_id|campaign_id|hsa_.*|irclickid|ranmid|ransiteid|s_kwcid|ef_id)$/i;
function isAdNetworkParam(key) {
    return exports.CLICK_ID_KEYS.includes(key.toLowerCase()) || AD_PARAM.test(key);
}
exports.isAdNetworkParam = isAdNetworkParam;
/**
 * Sanitize a landing URL's query: every key is kept (up to 30), values of
 * sensitive keys and sensitive-looking values become "[redacted]", values are
 * capped at 200 chars. Under GPC, click ids and ad-network params are dropped
 * entirely. Accepts a query string ("?a=1") or an entries list.
 */
function sanitizeQueryParams(query, options = {}) {
    const entries = typeof query === 'string' ? parseQuery(query) : query;
    const out = {};
    let count = 0;
    for (const [rawKey, rawValue] of entries) {
        const key = String(rawKey).slice(0, 64);
        if (!key || Object.prototype.hasOwnProperty.call(out, key))
            continue;
        if (options.gpc && isAdNetworkParam(key))
            continue;
        if (count >= exports.MAX_QUERY_PARAMS)
            break;
        const value = String(rawValue ?? '');
        out[key] = exports.SENSITIVE_PARAM_KEY.test(key) || looksSensitiveValue(value) ? exports.REDACTED : value.slice(0, exports.MAX_PROPERTY_STRING);
        count++;
    }
    return out;
}
exports.sanitizeQueryParams = sanitizeQueryParams;
function parseQuery(query) {
    // The fragment is never read: magic links put their secret in #token=.
    const q = query.split('#')[0].replace(/^[^?]*\?/, '').replace(/^\?/, '');
    if (!q)
        return [];
    const out = [];
    for (const part of q.split('&')) {
        if (!part)
            continue;
        const i = part.indexOf('=');
        const decode = (s) => { try {
            return decodeURIComponent(s.replace(/\+/g, ' '));
        }
        catch {
            return s;
        } };
        out.push(i < 0 ? [decode(part), ''] : [decode(part.slice(0, i)), decode(part.slice(i + 1))]);
    }
    return out;
}
/** Pathname only: query strings carry tokens, fragments carry magic-link secrets. */
function pathOnly(url) {
    return String(url || '').split(/[?#]/)[0].slice(0, 200);
}
exports.pathOnly = pathOnly;
/** origin + pathname of a referrer; '' when it isn't an absolute http(s) URL. */
function referrerParts(referrer) {
    const m = /^(https?:\/\/([^/?#:]+)(?::\d+)?)([^?#]*)/i.exec(String(referrer || ''));
    if (!m)
        return { referrerUrl: '', referrerDomain: '' };
    const domain = m[2].toLowerCase().replace(/^www\./, '');
    return { referrerUrl: `${m[1].toLowerCase()}${m[3] || '/'}`.slice(0, 200), referrerDomain: domain.slice(0, 100) };
}
exports.referrerParts = referrerParts;
exports.ANALYTICS_CHANNELS = ['paid_search', 'paid_social', 'organic_search', 'organic_social', 'email', 'sms', 'referral', 'direct', 'qr', 'clinic_link', 'other'];
const SEARCH_DOMAIN = /(^|\.)(google|bing|duckduckgo|yahoo|baidu|yandex|ecosia|brave|startpage|ask|aol|naver|qwant)\.[a-z.]+$/;
const SOCIAL_DOMAIN = /(^|\.)(facebook|fb|instagram|messenger|t|twitter|x|linkedin|lnkd|tiktok|pinterest|reddit|youtube|snapchat|threads|nextdoor|tumblr|whatsapp)\.[a-z.]+$/;
const SOCIAL_SOURCE = /^(facebook|fb|meta|instagram|ig|tiktok|linkedin|twitter|x|pinterest|reddit|snapchat|youtube|threads)$/;
const MAIL_DOMAIN = /(^|\.)(mail\.google\.com|outlook\.(live|office)\.com|mail\.yahoo\.com|mail\.aol\.com|mail\.proton\.me)$/;
function deriveChannel(input) {
    const medium = (input.utm_medium || '').toLowerCase().trim();
    const source = (input.utm_source || '').toLowerCase().trim();
    if (medium) {
        if (/^(cpc|ppc|paid|paidsearch|paid_search|paid-search|sem|search_ads)$/.test(medium))
            return SOCIAL_SOURCE.test(source) ? 'paid_social' : 'paid_search';
        if (/^(paid_?social|paid-social|social_?paid|social-paid|cpm|display_social)$/.test(medium))
            return 'paid_social';
        if (/^(e-?mail|newsletter)$/.test(medium))
            return 'email';
        if (/^(sms|text|mms)$/.test(medium))
            return 'sms';
        if (/^(qr|qr_?code|qr-code|print|offline)$/.test(medium))
            return 'qr';
        if (/^(social|organic_?social|social_?organic|social-network)$/.test(medium))
            return 'organic_social';
        if (/^(organic|seo)$/.test(medium))
            return 'organic_search';
        if (/^(referral|affiliate|partner)$/.test(medium))
            return 'referral';
        if (/^(clinic|clinic_?link|clinic-link)$/.test(medium))
            return 'clinic_link';
        if (medium === 'notification')
            return source === 'sms' ? 'sms' : 'email';
    }
    const clicks = (input.clickIds || []).map(c => c.toLowerCase());
    if (clicks.some(c => ['gclid', 'gbraid', 'wbraid', 'msclkid'].includes(c)))
        return 'paid_search';
    if (clicks.some(c => ['fbclid', 'ttclid', 'li_fat_id', 'twclid', 'epik', 'sccid'].includes(c)))
        return 'paid_social';
    if (medium || source)
        return 'other';
    const domain = (input.referrerDomain || '').toLowerCase().replace(/^www\./, '');
    if (domain && !(input.ownDomains || []).some(own => domain === own || domain.endsWith(`.${own}`))) {
        if (MAIL_DOMAIN.test(domain))
            return 'email';
        if (SEARCH_DOMAIN.test(domain))
            return 'organic_search';
        if (SOCIAL_DOMAIN.test(domain))
            return 'organic_social';
        return 'referral';
    }
    return 'direct';
}
exports.deriveChannel = deriveChannel;
//# sourceMappingURL=analyticsEvents.js.map